import { updateLaneEnrichment as updateLaneEnrichmentOp } from '@gql/operations'
import type { LaneStatus } from '../../types'
import type {
  UpdateLaneEnrichmentParams,
  UpdateLaneEnrichmentResult,
} from './types'

/**
 * Update a lane with enrichment data (route information, tolls, polyline, metadata).
 *
 * This is a low-level function that directly updates the lane record.
 * For full enrichment including route computation, use `enrichLane()` instead.
 *
 * @example
 * const result = await updateLaneEnrichment({
 *   laneId: 123,
 *   distanceMiles: 150.5,
 *   durationSeconds: 8400,
 *   tolls: 12.50,
 *   polyline: 'encoded_polyline_string',
 *   routeMetadata: {
 *     source: 'google_routes',
 *     computedAt: new Date().toISOString(),
 *   },
 *   enrichmentSource: 'google_routes',
 *   creationCriteria: {
 *     avoid_tolls: false,
 *     created_at: new Date().toISOString(),
 *   },
 *   status: 'ready',
 * })
 */
export async function updateLaneEnrichment(
  params: UpdateLaneEnrichmentParams
): Promise<UpdateLaneEnrichmentResult> {
  const {
    laneId,
    distanceMiles,
    durationSeconds,
    tolls,
    polyline,
    routeMetadata,
    enrichmentSource,
    creationCriteria,
    status,
  } = params

  const enrichedAt = new Date().toISOString()

  const updated = await updateLaneEnrichmentOp(
    {
      id: laneId,
      distanceMiles,
      durationSec: durationSeconds,
      tolls,
      routePolyline: polyline,
      routeMetadata: routeMetadata as unknown,
      enrichmentSource,
      creationCriteria: creationCriteria as unknown,
      status,
      enrichedAt,
    },
    { executor: params.executor }
  )

  if (!updated) {
    throw new Error(`Failed to update lane ${laneId}: lane not found`)
  }

  return {
    laneId: updated.id,
    affectedRows: 1,
    updatedat: updated.updatedat as string,
    status: updated.status as LaneStatus,
  }
}
